import React, { createContext, useContext, useMemo } from 'react';
import i18n from '../../i18n';
import { branchNameFor, formatTaskKey, parseTaskKey } from '../../extensions/bundled/task-manager/taskKeys';
import { copyTextToClipboard } from '../../utils/clipboard';
import { useNotificationStore } from '../../store/notificationStore';
import './TaskKeys.css';

/**
 * Task keys in the board UI (commit ↔ task links, slice 1). TaskBoardTab
 * computes one map task id → key for the open board (taskNumbers.taskKeysFor,
 * planned numbers included, so a viewer sees the same key as a writer) and
 * provides it here; the card, the list row and the task window read it.
 * Outside a board (the chat quick view) there is no provider → no key.
 */
export const TaskKeysContext = createContext(null);

/** The key of task `taskId` on the open board, or null. */
export function useTaskKey(taskId) {
  const keys = useContext(TaskKeysContext);
  return (keys && taskId != null && keys.get(String(taskId))) || null;
}

/** The open board's old prefixes (a renamed board, taskKeyPrefixEdit.js). */
export const TaskKeyAliasesContext = createContext([]);

/** The task's old keys (APB-4 after the board became WEB): commits written with them still link. */
export function useTaskAliasKeys(taskId) {
  const key = useTaskKey(taskId);
  const aliases = useContext(TaskKeyAliasesContext);
  return useMemo(() => {
    const parsed = parseTaskKey(key);
    if (!parsed || !aliases?.length) return [];
    return aliases.map((p) => formatTaskKey(p, parsed.number)).filter((k) => k && k !== key);
  }, [key, aliases]);
}

/** The small mono key ("APB-42") on a card, a list row and in the task window. */
export function TaskKeyChip({ taskKey, className = '' }) {
  if (!taskKey) return null;
  return (
    <span className={`task-key-chip${className ? ` ${className}` : ''}`} data-task-key={taskKey}>
      {taskKey}
    </span>
  );
}

async function copyWithToast(text, okKey) {
  const t = i18n.t.bind(i18n);
  const ok = await copyTextToClipboard(text);
  if (ok) useNotificationStore.getState().success(text, t(okKey));
  else useNotificationStore.getState().error(t('taskLinks.clipboardMissing'), t('taskLinks.notCopied'));
  return ok;
}

/** "Copy key" and "Copy branch name" rows for a task's menus (card ⋯ and the task window's …). */
export function taskKeyMenuItems(taskKey, title, icons = {}) {
  if (!taskKey) return [];
  const t = i18n.t.bind(i18n);
  const branch = branchNameFor(taskKey, title);
  return [
    { icon: icons.key || null, label: t('taskLinks.copyKey'), onClick: () => copyWithToast(taskKey, 'taskLinks.keyCopied') },
    branch && { icon: icons.branch || null, label: t('taskLinks.copyBranch'), onClick: () => copyWithToast(branch, 'taskLinks.branchCopied') },
  ].filter(Boolean);
}
